import { RouterProvider, createRouter } from "@tanstack/react-router";
import { Analytics } from "@vercel/analytics/react";
import { PostHogProvider } from "posthog-js/react";
import { PropsWithChildren, StrictMode } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";

// Import the generated route tree
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({ routeTree });

const environment = process.env.NODE_ENV || "development";
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || "";
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: (error, errorInfo) => {
      posthog.captureException(error, {
        componentStack: errorInfo.componentStack,
        environment,
      });
      console.warn("Uncaught error", error, errorInfo.componentStack);
    },

    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: (error, errorInfo) => {
      posthog.captureException(error, {
        componentStack: errorInfo.componentStack,
        environment,
      });
      console.warn("Caught error", error, errorInfo.componentStack);
    },

    // Callback called when React automatically recovers from errors.
    onRecoverableError: (error, errorInfo) => {
      posthog.captureException(error, {
        componentStack: errorInfo.componentStack,
        environment,
      });
      console.warn("Recoverable error", error, errorInfo.componentStack);
    },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000, // 30 seconds
        gcTime: 15 * 60 * 1000, // 15 minutes
      },
    },
  });

  const PostHogProviderWrapper = ({ children }: PropsWithChildren) => {
    if (environment !== "production") return <>{children}</>;

    return (
      <PostHogProvider
        apiKey={posthogKey}
        options={{
          api_host: posthogHost,
          disable_session_recording: environment !== "production",
          persistence: "memory",
        }}
      >
        {children}
      </PostHogProvider>
    );
  };

  root.render(
    <StrictMode>
      <PostHogProviderWrapper>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Analytics />
          {/* <ReactQueryDevtools /> */}
        </QueryClientProvider>
      </PostHogProviderWrapper>
    </StrictMode>
  );
}
